import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { payScheduleCreated, payScheduleUpdated } from './payScheduleSlice'
import {
    CreditorConnectionModel,
    EmployerModel,
    EmployerNameModel,
    PayArticleTypeModel
} from '../types'

interface EmployerState {
    loading: boolean
    employerNames: EmployerNameModel[]
    employerDetails: EmployerModel | null
    payArticleTypes: PayArticleTypeModel[]
    payArticleTypesLoading: boolean
    payArticleTypesError: Error | string | null
    createdEmployerId: string | null
    error: Error | string | null
}

const initialState: EmployerState = {
    loading: false,
    employerNames:  [],
    employerDetails: null,
    payArticleTypes: [],
    payArticleTypesLoading: false,
    payArticleTypesError: null,
    createdEmployerId: null,
    error: null
}

export const employerSlice = createSlice({
    name: 'employer',
    initialState,
    reducers: {
        loading: state => {
            state.loading = true
            state.error = null
        },
        employerDetailsFetched: (state, action: PayloadAction<EmployerModel>) => {
            state.loading = false
            state.employerDetails = action.payload
            state.error = null
        },
        employerDetailsFetchFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerNamesFetched: (state, action: PayloadAction<EmployerNameModel[]>) => {
            state.loading = false
            state.employerNames = action.payload
            state.error = null
        },
        employerNamesFetchFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        payArticleTypesLoading: state => {
            state.payArticleTypesLoading = true
            state.payArticleTypesError = null
        },
        payArticleTypesFetched: (state, action: PayloadAction<PayArticleTypeModel[]>) => {
            state.payArticleTypesLoading = false
            state.payArticleTypes = action.payload
            state.payArticleTypesError = null
        },
        payArticleTypeOperationSucceeded: state => {
            state.payArticleTypesLoading = false
            state.payArticleTypesError = null
        },
        payArticleTypesFetchFailure: (state, action: PayloadAction<Error | string>) => {
            state.payArticleTypesLoading = false
            state.payArticleTypesError = action.payload
        },
        employerCreated: (state, action: PayloadAction<string>) => {
            state.loading = false
            state.createdEmployerId = action.payload
            state.error = null
        },
        employerCreationHandled: state => {
            state.createdEmployerId = null
        },
        employerCreateFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerInfoUpdated: (state, action: PayloadAction<EmployerModel>) => {
            state.loading = false
            state.employerDetails = action.payload
            state.error = null
        },
        employerInfoUpdateFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerPayoutSettingsUpdated: (state, action: PayloadAction<EmployerModel>) => {
            state.loading = false
            state.employerDetails = action.payload
            state.error = null
        },
        employerPayoutSettingsUpdateFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerCreditSpaceWarningSilenced: (state, action: PayloadAction<EmployerModel>) => {
            state.loading = false
            state.employerDetails = action.payload
            state.error = null
        },
        employerCreditSpaceWarningSilenceFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        bankAccountSet: (state, action: PayloadAction<{bankAccountId: string, bankAccountNumber: string}>) => {
            state.loading = false
            state.employerDetails = {
                ...state.employerDetails as EmployerModel,
                bankAccount: {id: action.payload.bankAccountId, bankAccountNumber: action.payload.bankAccountNumber}
              }
            state.error = null
        },
        bankAccountSetFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerCreditorAdded: (state, action: PayloadAction<CreditorConnectionModel>) => {
            state.loading = false
            state.employerDetails = {
                ...state.employerDetails as EmployerModel,
                creditors: [action.payload, ...state.employerDetails?.creditors ?? []]
              }
            state.error = null
        },
        employerCreditorAddFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerCreditorDeleted: (state, action: PayloadAction<string>) => {
            var index = state.employerDetails?.creditors.findIndex((item) => {
                return item.id === action.payload
            })
            const creditors = [...state.employerDetails?.creditors ?? []]
            creditors.splice(index ?? 0, 1)

            state.loading = false
            state.employerDetails = {
                ...state.employerDetails as EmployerModel,
                creditors: creditors
              }
            state.error = null
        },
        employerCreditorDeleteFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerLogoUploaded: (state, action: PayloadAction<EmployerModel>) => {
            state.loading = false
            state.employerDetails = action.payload
            state.error = null
        },
        employerLogoUploadFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
        employerLogoDeleted: (state, action: PayloadAction<EmployerModel>) => {
            state.loading = false
            state.employerDetails = action.payload
            state.error = null
        },
        employerLogoDeleteFailure: (state, action: PayloadAction<Error | string>) => {
            state.loading = false
            state.error = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
          .addCase(payScheduleCreated, (state, action) => {
              state.employerDetails = null
              state.payArticleTypes = []
              state.payArticleTypesLoading = false
              state.payArticleTypesError = null
          })
          .addCase(payScheduleUpdated, (state, action) => {
              state.employerDetails = null
              state.payArticleTypes = []
              state.payArticleTypesLoading = false
              state.payArticleTypesError = null
          })
    }
})

export const {
    loading,
    employerDetailsFetched,
    employerDetailsFetchFailure,
    employerNamesFetched,
    employerNamesFetchFailure,
    payArticleTypesLoading,
    payArticleTypesFetched,
    payArticleTypeOperationSucceeded,
    payArticleTypesFetchFailure,
    employerCreated,
    employerCreationHandled,
    employerCreateFailure,
    employerInfoUpdated,
    employerInfoUpdateFailure,
    employerPayoutSettingsUpdated,
    employerPayoutSettingsUpdateFailure,
    employerCreditSpaceWarningSilenced,
    employerCreditSpaceWarningSilenceFailure,
    bankAccountSet,
    bankAccountSetFailure,
    employerCreditorAdded,
    employerCreditorAddFailure,
    employerCreditorDeleted,
    employerCreditorDeleteFailure,
    employerLogoUploaded,
    employerLogoUploadFailure,
    employerLogoDeleted,
    employerLogoDeleteFailure
} = employerSlice.actions
export default employerSlice.reducer
